import type {
  ActivityFeedResponse,
  CarePlanResponse,
  CareTeamMember,
  GenerateOtcResponse,
  GetPresignedUrlForGetPayload,
  GetPresignedUrlForGetResponse,
  GetPresignedUrlForPostPayload,
  GetPresignedUrlForPostResponse,
  Note,
  NoteDetail,
  PatientDetailsResponse,
  PatientDiagnosis,
  UUID,
} from "@/core/types";
import type { ApiRequestWith } from "@/api/types";
import { getGroupAuthType } from "@/utils/authToken";
import { GROUP_AUTH_HEADER_NAME } from "@/api/constants";
import { httpAuth2 } from "@/api/auth";
import type { PatientFile } from "@/features/patient/types/PatientFile";

type SharedRequestsTyped = {
  fetch_patient_careteam: ApiRequestWith<
    CareTeamMember[],
    { searchParams?: Record<string, string>; urlParams: { patientId: string } }
  >;
  fetch_patient_details: ApiRequestWith<
    PatientDetailsResponse,
    { urlParams: { patientId: string } }
  >;
  fetch_patient_progress_notes: ApiRequestWith<
    NoteDetail,
    { searchParams: { note_id: UUID }; urlParams: { patientId: UUID } }
  >;
  fetch_patient_progress_notes_batch: ApiRequestWith<
    NoteDetail[],
    { searchParams: { note_ids: UUID[] }; urlParams: { patientId: UUID } }
  >;
  generate_otc: ApiRequestWith<GenerateOtcResponse, { searchParams: { patient_id: string } }>;
  get_activity_feed: ApiRequestWith<ActivityFeedResponse, { urlParams: { patientId: string } }>;
  get_care_plan: ApiRequestWith<CarePlanResponse, { urlParams: { patientId: string } }>;
  get_files: ApiRequestWith<PatientFile[], { body: { entity_id: string; entity_type: string } }>;
  get_notes_summary: ApiRequestWith<Note[], { urlParams: { patientId: string } }>;
  get_patient_diagnosis: ApiRequestWith<PatientDiagnosis, { urlParams: { patientId: string } }>;
  get_presigned_url: ApiRequestWith<
    GetPresignedUrlForGetResponse,
    { body: GetPresignedUrlForGetPayload }
  >;
  post_presigned_url: ApiRequestWith<
    GetPresignedUrlForPostResponse,
    { body: GetPresignedUrlForPostPayload }
  >;
  send_on_demand_welcome_email: ApiRequestWith<void, { urlParams: { patientId: string } }>;
};

export const sharedRequestsTyped: SharedRequestsTyped = {
  fetch_patient_careteam: function ({ context, searchParams = {}, urlParams }) {
    return httpAuth2
      .post(`/v1/patient_management/${urlParams.patientId}/care_team`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams,
      })
      .json<CareTeamMember[]>();
  },

  fetch_patient_details: function ({ context, urlParams }) {
    return httpAuth2
      .post(`/v1/patient_management/${urlParams.patientId}/patient_details`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<PatientDetailsResponse>();
  },

  fetch_patient_progress_notes: function ({ context, searchParams, urlParams }) {
    return httpAuth2
      .post(`/v1/patient_management/${urlParams.patientId}/get_progress_notes`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams,
      })
      .json<NoteDetail>();
  },

  fetch_patient_progress_notes_batch: function ({ context, searchParams, urlParams }) {
    return httpAuth2
      .post(`/v1/patient_management/${urlParams.patientId}/get_progress_notes`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        // cast needed: ky's SearchParamsOption doesn't allow array values, but the
        // backend accepts repeated params via note_ids[] — same runtime behaviour as the JS version
        searchParams: searchParams as unknown as Record<string, string>,
      })
      .json<NoteDetail[]>();
  },

  generate_otc: function ({ context, searchParams }) {
    return httpAuth2
      .post("/v1/auth/generate_otc", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams,
      })
      .json<GenerateOtcResponse>();
  },

  get_activity_feed: function ({ context, urlParams }) {
    return httpAuth2
      .post(`/v1/patient_management/${urlParams.patientId}/activity`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<ActivityFeedResponse>();
  },

  get_care_plan: function ({ context, urlParams }) {
    return httpAuth2
      .post(`/v1/care-plan/${urlParams.patientId}/get_care_plan`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<CarePlanResponse>();
  },

  get_files: function ({ body, context }) {
    return httpAuth2
      .post("/v1/provider/get_files", {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: body,
      })
      .json<PatientFile[]>();
  },

  get_notes_summary: function ({ context, urlParams }) {
    return httpAuth2
      .post(`/v1/patient_management/${urlParams.patientId}/get_notes_summary`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<Note[]>();
  },

  get_patient_diagnosis: function ({ context, urlParams }) {
    return httpAuth2
      .post(`/v1/patient_management/${urlParams.patientId}/get_patient_diagnosis`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<PatientDiagnosis>();
  },

  get_presigned_url: function ({ body, context }) {
    return httpAuth2
      .post(`/v4/provider/get_presigned_url`, {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: body,
      })
      .json<GetPresignedUrlForGetResponse>();
  },

  post_presigned_url: function ({ body, context }) {
    return httpAuth2
      .post(`/v4/provider/get_presigned_url`, {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: body,
      })
      .json<GetPresignedUrlForPostResponse>();
  },

  send_on_demand_welcome_email: function ({ context, urlParams }) {
    return httpAuth2
      .post(`/v1/patient_management/${urlParams.patientId}/send_on_demand_welcome_email`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<void>();
  },
};
